import type { AlertCategory, AlertSummary } from "@/features/alerts/types";
import { apiFetch } from "@/lib/api";

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiGroup {
  client: string | null;
  client_name: string | null;
  category: AlertCategory;
  total_count: number;
  new_count: number;
  latest_at: string;
}

/** GET /alerts/summary/ — grouped alert feed. "new" counts are computed
 * server-side against the user's stored per-category last-view marker, so no
 * client timestamp is passed. */
export async function getAlertSummary(): Promise<AlertSummary> {
  const body = await apiFetch<
    Envelope<{ groups: ApiGroup[]; total: number; total_new: number }>
  >("/alerts/summary/");
  return {
    groups: body.data.groups.map((g) => ({
      client: g.client,
      clientName: g.client_name,
      category: g.category,
      totalCount: g.total_count,
      newCount: g.new_count,
      latestAt: g.latest_at,
    })),
    total: body.data.total,
    totalNew: body.data.total_new,
  };
}

/** POST /alerts/mark-viewed/ — advance the "last viewed" marker for a category
 * so its rows stop counting as new on the next load. */
export async function markAlertsViewed(category: AlertCategory): Promise<void> {
  await apiFetch("/alerts/mark-viewed/", {
    method: "POST",
    json: { category },
  });
}

/** POST /alerts/:id/dismiss/ — remove an alert from the feed. */
export async function dismissAlert(id: string): Promise<void> {
  await apiFetch(`/alerts/${id}/dismiss/`, { method: "POST" });
}
